import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Row, Col, Card } from "reactstrap";
import ReactTable from "react-table";
import { connect } from "react-redux";
import fetchVariationChildService from "../../../../../Service/OPSServices/AmazonProductListing_v1/fetchVariationChildService";
import bulkUpdateVariationService from "../../../../../Service/OPSServices/AmazonProductListing_v1/bulkUpdateVariationService";

// Strict Select Cell — custom dropdown, works outside table overflow
function StrictSelectCell({ attr, initialVal, allowedValues, allowedLabels, asin, onAttrChange, submitError }) {
    const [localVal, setLocalVal] = useState(initialVal || "");
    const [isOpen, setIsOpen] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);

    const prevRef = useRef(initialVal);
    useEffect(() => {
        if (prevRef.current !== initialVal) {
            prevRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Close on page scroll, but not on dropdown internal scroll
    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = (e) => {
            if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            setIsOpen(false);
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, [isOpen]);

    const checkDropDirection = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setDropUp(window.innerHeight - rect.bottom < 250);
        }
    };

    const selectedLabel = allowedValues.reduce((found, opt, i) => {
        if (String(opt) === String(localVal)) return (allowedLabels && allowedLabels[i]) || String(opt);
        return found;
    }, null);

    return (
        <div ref={wrapperRef} style={{ width: "100%", position: "relative" }}>
            <div
                onClick={() => { checkDropDirection(); setIsOpen((prev) => !prev); }}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 24px 2px 6px",
                    width: "100%", border: submitError ? "1px solid #dc2626" : "1px solid #ccc",
                    background: "#fff", color: localVal ? "#111" : "#999", cursor: "pointer",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    userSelect: "none", minHeight: 24,
                }}
            >
                <span>{selectedLabel || localVal || "-- select --"}</span>
                <span style={{ fontSize: 10, color: "#666" }}>▼</span>
            </div>
            {submitError && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {submitError}
                </small>
            )}
            {isOpen && (
                <div ref={dropdownRef} style={{
                    position: "fixed", background: "#fff", border: "1px solid #d5d9d9",
                    borderRadius: 8, maxHeight: 220, overflowY: "auto", zIndex: 99999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)", minWidth: 150,
                    ...(dropUp
                        ? {
                            bottom: wrapperRef.current ? window.innerHeight - wrapperRef.current.getBoundingClientRect().top + 2 : "auto",
                            left: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().left : 0,
                        }
                        : {
                            top: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().bottom + 2 : "auto",
                            left: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().left : 0,
                        }
                    ),
                }}>
                    <div
                        onMouseDown={() => { setLocalVal(""); onAttrChange(asin, attr, ""); setIsOpen(false); }}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, color: "#999", background: localVal === "" ? "#e8f4fd" : "#fff" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = localVal === "" ? "#e8f4fd" : "#fff"; }}
                    >-- select --</div>
                    {allowedValues.map((opt, i) => {
                        const label = (allowedLabels && allowedLabels[i]) || String(opt);
                        const isSelected = String(opt) === String(localVal);
                        return (
                            <div
                                key={String(opt)}
                                onMouseDown={() => { setLocalVal(String(opt)); onAttrChange(asin, attr, String(opt)); setIsOpen(false); }}
                                style={{ padding: "8px 12px", cursor: "pointer", fontSize: 12, background: isSelected ? "#e8f4fd" : "#fff", color: "#111", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#e8f4fd" : "#fff"; }}
                            >{label}</div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Suggestive input — free text with dropdown suggestions
function SuggestiveAttrInput({ attr, initialVal, allowedValues, deprecatedValues, maxLength, maxUtf8, asin, onAttrChange, submitError }) {
    const [localVal, setLocalVal] = useState(initialVal || "");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    const prevInitialRef = useRef(initialVal);
    useEffect(() => {
        if (prevInitialRef.current !== initialVal) {
            prevInitialRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    // Close on page scroll, but not on dropdown internal scroll
    useEffect(() => {
        if (!showSuggestions) return;
        const handleScroll = (e) => {
            if (inputRef.current && inputRef.current.contains(e.target)) return;
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            setShowSuggestions(false);
        };
        window.addEventListener("scroll", handleScroll, true);
        return () => window.removeEventListener("scroll", handleScroll, true);
    }, [showSuggestions]);

    const checkDropDirection = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropUp(window.innerHeight - rect.bottom < 250);
        }
    };

    // Real-time length validation
    const currentLen = localVal.length;
    const currentUtf8Len = new TextEncoder().encode(localVal).length;
    const isTooLong = (maxLength && currentLen > maxLength) || (maxUtf8 && !maxLength && currentUtf8Len > maxUtf8);

    // Real-time deprecated check
    const isDeprecated = deprecatedValues && deprecatedValues.length > 0 &&
        deprecatedValues.some((d) => String(d).toLowerCase() === String(localVal).toLowerCase());

    const allOptions = localVal && !allowedValues.includes(localVal) ? [localVal, ...allowedValues] : allowedValues;
    const filteredOptions = allOptions.filter(opt =>
        String(opt).toLowerCase().includes(String(localVal || "").toLowerCase())
    );

    return (
        <div style={{ width: "100%", position: "relative" }}>
            <input
                ref={inputRef}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 6px", width: "100%",
                    border: (isTooLong || submitError) ? "1px solid #dc2626" : "1px solid #ccc",
                    background: "#fff", color: "#111"
                }}
                value={localVal}
                onFocus={() => { checkDropDirection(); setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onChange={(e) => { setLocalVal(e.target.value); onAttrChange(asin, attr, e.target.value); setShowSuggestions(true); }}
                placeholder={`Select ${attr}...`}
                autoComplete="off"
            />
            {isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {maxLength ? `Maximum ${maxLength} characters allowed` : `Maximum ${maxUtf8} UTF-8 bytes allowed`}
                </small>
            )}
            {isDeprecated && !isTooLong && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3, fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 4, padding: "2px 6px" }}>
                    <span>⚠️</span>
                    <span>This value is <strong>deprecated</strong> by Amazon.</span>
                </div>
            )}
            {submitError && !isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {submitError}
                </small>
            )}
            {showSuggestions && filteredOptions.length > 0 && (
                <div ref={dropdownRef} style={{
                    position: "fixed",
                    ...(dropUp
                        ? {
                            bottom: inputRef.current ? window.innerHeight - inputRef.current.getBoundingClientRect().top + 2 : "auto",
                            left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                        }
                        : {
                            top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + 2 : "auto",
                            left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                        }
                    ),
                    width: inputRef.current ? inputRef.current.getBoundingClientRect().width : 150,
                    background: "#fff", border: "1px solid #d5d9d9", borderRadius: 8,
                    maxHeight: 220, overflowY: "auto", zIndex: 99999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}>
                    {filteredOptions.slice(0, 50).map((opt) => (
                        <div
                            key={String(opt)}
                            onMouseDown={() => { setLocalVal(String(opt)); onAttrChange(asin, attr, String(opt)); setShowSuggestions(false); }}
                            style={{ padding: "10px 12px", cursor: "pointer", fontSize: 12, background: "#fff", color: "#111" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                        >{String(opt)}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Plain text input cell
function EditableCell({ attr, initialVal, asin, onAttrChange, maxLength, maxUtf8, submitError }) {
    const [localVal, setLocalVal] = useState(initialVal || "");

    const prevInitialRef = useRef(initialVal);
    useEffect(() => {
        if (prevInitialRef.current !== initialVal) {
            prevInitialRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    const currentLen = localVal.length;
    const currentUtf8Len = new TextEncoder().encode(localVal).length;
    const isTooLong = (maxLength && currentLen > maxLength) || (maxUtf8 && !maxLength && currentUtf8Len > maxUtf8);

    return (
        <div style={{ width: "100%" }}>
            <input
                type="text"
                value={localVal}
                onChange={(e) => { setLocalVal(e.target.value); onAttrChange(asin, attr, e.target.value); }}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 6px", width: "100%",
                    border: (isTooLong || submitError) ? "1px solid #dc2626" : "1px solid #ccc"
                }}
                placeholder={`Enter ${attr}`}
            />
            {isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {maxLength ? `Maximum ${maxLength} characters allowed` : `Maximum ${maxUtf8} UTF-8 bytes allowed`}
                </small>
            )}
            {submitError && !isTooLong && (
                <small style={{ color: "#dc2626", fontSize: 11, display: "block", marginTop: 2 }}>
                    {submitError}
                </small>
            )}
        </div>
    );
}

// Confirm modal — shows edited rows before final update
function ConfirmUpdateModal({ editedRows, varAttrs, onConfirm, onCancel, submitting }) {
    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: 10, width: 560, maxWidth: "95vw", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#111" }}>
                        Confirm Update — {editedRows.length} product{editedRows.length > 1 ? "s" : ""}
                    </span>
                    <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1 }}>×</button>
                </div>
                <div style={{ overflowY: "auto", flex: 1, padding: "12px 18px" }}>
                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                        The following child products will be updated. Once confirmed, changes will be patched to Amazon.
                    </p>
                    {editedRows.map((row) => (
                        <div key={row.asin} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginBottom: 8, background: "#f9fafb" }}>
                            <div style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#1a6bb5", marginBottom: 6 }}>
                                {row.asin}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {varAttrs.map((attr) => {
                                    const val = row.editedAttrs[attr];
                                    if (val === undefined || val === null) return null;
                                    return (
                                        <span key={attr} style={{ fontSize: 11, background: "#e8f4fd", border: "1px solid #b3d7f5", borderRadius: 4, padding: "2px 8px", color: "#1a6bb5" }}>
                                            <b>{attr}:</b> {String(val) || <i style={{ color: "#999" }}>empty</i>}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                        onClick={onCancel}
                        disabled={submitting}
                        style={{ background: "#fff", border: "1px solid #d1d5db", color: "#374151", borderRadius: 6, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}
                    >Cancel</button>
                    <button
                        // onClick={onConfirm}
                        disabled={submitting}
                        style={{ background: submitting ? "#93c5fd" : "#0073bb", border: "none", color: "#fff", borderRadius: 6, padding: "7px 20px", fontSize: 13, fontWeight: 500, cursor: submitting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}
                    >
                        {submitting ? (
                            <>
                                <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                                Updating...
                            </>
                        ) : "Update Listing"}
                    </button>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

function VariationsChildGrid(props) {
    const [variationData, setVariationData] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editedAttrs, setEditedAttrs] = useState({});
    const [submitErrors, setSubmitErrors] = useState({});
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");

    useEffect(() => {
        if (!props.selectedAsin) return;
        setLoading(true);
        setError(null);
        setEditedAttrs({});
        setSubmitErrors({});
        setSuccessMsg("");

        const data = {
            asin: props.selectedAsin,
            sku: props.sku || "",
            variation_theme: props.prefetchedVariationTheme || null,
        };
        fetchVariationChildService(props.token, data).then((res) => {
            if (res && res.status) {
                setVariationData(res);
            } else {
                setError((res && res.message) || "Failed to load variation data.");
            }
            setLoading(false);
        });
    }, [props.selectedAsin, props.sku, props.prefetchedVariationTheme]);

    const handleAttrChange = useCallback((asin, attrKey, value) => {
        setEditedAttrs((prev) => ({
            ...prev,
            [asin]: { ...(prev[asin] || {}), [attrKey]: value },
        }));
        // Clear submit error for this cell when user edits
        setSubmitErrors((prev) => {
            if (!prev[asin] || !prev[asin][attrKey]) return prev;
            const updated = { ...prev, [asin]: { ...prev[asin] } };
            delete updated[asin][attrKey];
            return updated;
        });
    }, []);

    // Resolve field meta from schema — field type, validation rules
    const getAttrFieldMeta = useCallback((attrKey) => {
        const empty = { allowedValues: [], allowedLabels: [], isSuggestive: false, isStrictSelect: false, maxLength: null, maxUtf8: null, deprecatedValues: [], isRequired: false };
        if (!props.schemaFields) return empty;
        const field = props.schemaFields.find(
            (f) => f.field === attrKey || f.field === attrKey.toLowerCase()
        );
        if (!field) return empty;

        const options     = (field.schema && field.schema.allowed_values) || [];
        const allowCustom = field.schema && field.schema.rules && field.schema.rules.allow_custom_values;
        const isDropdown  = field.render && field.render.component === "SELECT";
        const rules       = (field.schema && field.schema.rules) || {};

        return {
            allowedValues:   options,
            allowedLabels:   (field.schema && field.schema.allowed_value_labels) || [],
            isSuggestive:    options.length > 0 && allowCustom === true,
            isStrictSelect:  isDropdown && options.length > 0 && allowCustom !== true,
            maxLength:       rules.maxLength || null,
            maxUtf8:         rules.maxUtf8ByteLength || null,
            deprecatedValues:(field.schema && field.schema.deprecated_values) || [],
            isRequired:      rules.required === true,
        };
    }, [props.schemaFields]);

    const children = ((variationData && variationData.children) || []).filter((c) => !c.is_parent);
    const varAttrs = (variationData && variationData.variation_attributes) || [];
    const theme    = (variationData && variationData.variation_theme) || "";

    const filteredChildren = children.filter((row) => {
        const text  = searchText.toLowerCase();
        const asin  = (row.asin || "").toLowerCase();
        const title = (row.title || "").toLowerCase();
        const skus  = (row.skus || []).join(" ").toLowerCase();
        const attrs = Object.values(row.attributes || {}).join(" ").toLowerCase();
        return asin.includes(text) || title.includes(text) || skus.includes(text) || attrs.includes(text);
    });

    // Validate all rows on submit click
    const handleSubmitClick = useCallback(() => {
        const errors = {};
        children.forEach((row) => {
            varAttrs.forEach((attr) => {
                const meta = getAttrFieldMeta(attr);
                const val = (editedAttrs[row.asin] && editedAttrs[row.asin][attr] !== undefined)
                    ? editedAttrs[row.asin][attr]
                    : (row.attributes && row.attributes[attr]) || "";
                const valStr = String(val || "").trim();

                if (meta.isRequired && !valStr) {
                    if (!errors[row.asin]) errors[row.asin] = {};
                    errors[row.asin][attr] = "This field is required";
                    return;
                }
                if (meta.maxLength && valStr.length > meta.maxLength) {
                    if (!errors[row.asin]) errors[row.asin] = {};
                    errors[row.asin][attr] = `Maximum ${meta.maxLength} characters allowed`;
                    return;
                }
                if (meta.maxUtf8 && !meta.maxLength) {
                    const utf8len = new TextEncoder().encode(valStr).length;
                    if (utf8len > meta.maxUtf8) {
                        if (!errors[row.asin]) errors[row.asin] = {};
                        errors[row.asin][attr] = `Maximum ${meta.maxUtf8} UTF-8 bytes allowed`;
                        return;
                    }
                }
            });
        });

        if (Object.keys(errors).length > 0) {
            setSubmitErrors(errors);
            return;
        }

        setSubmitErrors({});
        setShowConfirm(true);
    }, [children, varAttrs, editedAttrs, getAttrFieldMeta]);

    // Only edited rows for confirm modal
    const editedRows = useMemo(() => {
        return children
            .filter((row) => editedAttrs[row.asin] && Object.keys(editedAttrs[row.asin]).length > 0)
            .map((row) => ({ asin: row.asin, editedAttrs: editedAttrs[row.asin] }));
    }, [children, editedAttrs]);

    // Call bulk update service after confirm
    const handleConfirmUpdate = useCallback(async () => {
        setSubmitting(true);
        try {
            const payload = children
                .filter((row) => editedAttrs[row.asin])
                .map((row) => ({
                    asin: row.asin,
                    skus: row.skus || [],
                    attributes: { ...editedAttrs[row.asin] },
                }));
    
            const res = await bulkUpdateVariationService(props.token, payload);
    
            if (!res || !res.status) {
                setError(res.message || "Update failed. Please try again.");
                setShowConfirm(false);
                return;
            }
    
            setShowConfirm(false);
            setEditedAttrs({});
            setSuccessMsg(`Feed submitted for ${payload.length} product${payload.length > 1 ? "s" : ""}. Amazon is processing the update.`);
            setTimeout(() => setSuccessMsg(""), 4000);
        } catch (err) {
            setError("Update failed. Please try again.");
            setShowConfirm(false);
        } finally {
            setSubmitting(false);
        }
    }, [children, editedAttrs, props.token]);

    const columns = useMemo(() => [
        {
            Header: "ASIN",
            accessor: "asin",
            width: 150,
            Cell: (cellInfo) => (
                <div className="d-flex justify-content-center w-100">
                    <p className="mb-0" style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {cellInfo.value}
                    </p>
                </div>
            ),
        },
        {
            Header: "SKU",
            accessor: "sku",
            width: 250,
            Cell: (cellInfo) => (
                <div className="d-flex w-100">
                    {cellInfo.value ? (
                        <span style={{
                            fontFamily: "monospace", fontSize: 11,
                            background: "#f3f4f6", border: "1px solid #dde1e7",
                            borderRadius: 4, padding: "1px 6px", color: "#374151",
                        }}>
                            {cellInfo.value}
                        </span>
                    ) : (
                        <p className="mb-0 text-muted">—</p>
                    )}
                </div>
            ),
        },
        {
            Header: "Product Title",
            accessor: "title",
            Cell: (cellInfo) => (
                <div className="d-flex justify-content-start w-100">
                    <p className="mb-0">{cellInfo.value || "—"}</p>
                </div>
            ),
        },
        ...varAttrs.map((attr) => ({
            Header: attr.charAt(0).toUpperCase() + attr.slice(1).replace(/_/g, " "),
            accessor: attr,
            width: 180,
            Cell: (cellInfo) => {
                const currentVal = (cellInfo.original.attributes && cellInfo.original.attributes[attr]) || "";
                const { allowedValues, allowedLabels, isSuggestive, isStrictSelect, maxLength, maxUtf8, deprecatedValues } = getAttrFieldMeta(attr);
                const submitError = submitErrors[cellInfo.original.asin] && submitErrors[cellInfo.original.asin][attr];

                return (
                    <div className="d-flex justify-content-center w-100">
                        {isStrictSelect ? (
                            <StrictSelectCell
                                key={`${cellInfo.original.asin}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                allowedValues={allowedValues}
                                allowedLabels={allowedLabels}
                                asin={cellInfo.original.asin}
                                onAttrChange={handleAttrChange}
                                submitError={submitError}
                            />
                        ) : isSuggestive ? (
                            <SuggestiveAttrInput
                                key={`${cellInfo.original.asin}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                allowedValues={allowedValues}
                                deprecatedValues={deprecatedValues}
                                maxLength={maxLength}
                                maxUtf8={maxUtf8}
                                asin={cellInfo.original.asin}
                                onAttrChange={handleAttrChange}
                                submitError={submitError}
                            />
                        ) : (
                            <EditableCell
                                key={`${cellInfo.original.asin}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                asin={cellInfo.original.asin}
                                onAttrChange={handleAttrChange}
                                maxLength={maxLength}
                                maxUtf8={maxUtf8}
                                submitError={submitError}
                            />
                        )}
                    </div>
                );
            },
        })),
    ], [varAttrs, getAttrFieldMeta, handleAttrChange, submitErrors]);

    if (error) {
        return (
            <div className="mb-3 p-3" style={{
                background: "#fff3f3", border: "1px solid #ffb3b3",
                borderRadius: 8, color: "#cc0000", fontSize: 13,
            }}>
                {error}
            </div>
        );
    }

    const hasEdits = Object.keys(editedAttrs).length > 0;
    const totalErrors = Object.values(submitErrors).reduce((sum, obj) => sum + Object.keys(obj).length, 0);

    return (
        <Card className="card-of-problemarea p-3 mb-4 mt-4 shadow-sm border-0">
            <Row className="mb-3 align-items-center">
                <Col md={7}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <h4 className="listing-page-title mb-0" style={{ fontWeight: "bold" }}>
                            Child Products
                        </h4>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f0f4f8", border: "1px solid #d0d7de", borderRadius: 20, padding: "3px 12px", fontSize: 12, color: "#374151", fontWeight: 500 }}>
                            Theme :&nbsp;<b style={{ color: "#1a6bb5" }}>{theme}</b>
                        </span>
                        {variationData && variationData.parent_asin && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f0f4f8", border: "1px solid #d0d7de", borderRadius: 20, padding: "3px 12px", fontSize: 12, color: "#374151", fontWeight: 500 }}>
                                Parent ASIN :&nbsp;
                                <b style={{ fontFamily: "monospace", color: "#1a6bb5" }}>{variationData.parent_asin}</b>
                            </span>
                        )}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f0f4f8", border: "1px solid #d0d7de", borderRadius: 20, padding: "3px 12px", fontSize: 12, color: "#374151", fontWeight: 500 }}>
                            Child :&nbsp;<b>{children.length}</b>
                        </span>
                    </div>
                </Col>
                <Col md={5} className="d-flex justify-content-end align-items-center" style={{ gap: 8 }}>
                    <input
                        type="text"
                        placeholder="Select..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="form-control w-50"
                    />
                    <button
                        onClick={handleSubmitClick}
                        disabled={!hasEdits || submitting}
                        style={{
                            background: !hasEdits ? "#e5e7eb" : "#0073bb",
                            color: !hasEdits ? "#9ca3af" : "#fff",
                            border: "none", borderRadius: 6,
                            padding: "6px 16px", fontSize: 13, fontWeight: 500,
                            cursor: !hasEdits ? "not-allowed" : "pointer",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Submit
                    </button>
                </Col>
            </Row>

            {totalErrors > 0 && (
                <div style={{ background: "#fff3f3", border: "1px solid #ffb3b3", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#cc0000", display: "flex", alignItems: "center", gap: 6 }}>
                    ⚠️ <b>{totalErrors} validation error{totalErrors > 1 ? "s" : ""} found.</b> Please fix the highlighted fields.
                </div>
            )}

            {successMsg && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#166534", display: "flex", alignItems: "center", gap: 6 }}>
                    ✅ {successMsg}
                </div>
            )}

            <Row>
                <Col md={12}>
                    <ReactTable
                        data={filteredChildren}
                        columns={columns}
                        loading={loading}
                        className="-highlight -striped"
                        getTheadProps={() => ({
                            style: { overflowY: "hidden", background: "#B1FA63", color: "#243837" },
                        })}
                        getTrProps={(state, rowInfo) => {
                            if (!rowInfo) return {};
                            const hasRowError = submitErrors[rowInfo.original.asin] && Object.keys(submitErrors[rowInfo.original.asin]).length > 0;
                            return {
                                style: {
                                    background: hasRowError ? "#fff5f5" : rowInfo.original.is_parent ? "#fffbeb" : undefined,
                                    fontStyle: rowInfo.original.is_parent ? "italic" : "normal",
                                    overflow: "visible",
                                },
                            };
                        }}
                        defaultPageSize={5}
                        noDataText={loading ? "Loading..." : "No Data Found."}
                    />
                </Col>
            </Row>

            {showConfirm && (
                <ConfirmUpdateModal
                    editedRows={editedRows}
                    varAttrs={varAttrs}
                    onConfirm={handleConfirmUpdate}
                    onCancel={() => setShowConfirm(false)}
                    submitting={submitting}
                />
            )}
        </Card>
    );
}

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
});

export default connect(mapStateToProps)(VariationsChildGrid);